import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { arrayToObject, deleteParamsFromUrl } from 'core-components/helpers/url'
import { ConfirmDialog } from 'core-components/library/confirmDialog'
import {
  DelayedOperationController,
  useDelayedOperationContext,
} from 'core-components/library/delayedOperationController'
import { Portal, useCloseByClickHandler } from 'core-components/library/popup'

import {
  CONFIRM_BUTTON_CANCEL_KEY,
  CONFIRM_BUTTON_NO_KEY,
  CONFIRM_BUTTON_YES_KEY,
} from './constants/confirmButtonsKeys'
import { ModalPaper } from './ModalPaper'
import { IModalContent } from './models/IModalContent'
import { IModalDialogProps } from './models/IModalDialogProps'

export const ModalDialog = <T,>(props: IModalDialogProps<T>) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const { popupAttributeData } = useCloseByClickHandler({
    visible: true,
    useOwnContext: true,
    rootRef: rootRef,
  })

  return (
    <div ref={rootRef} style={{ display: 'contents' }} data-testid="modalDialogRoot">
      <DelayedOperationController>
        <ModalDialogComponent {...props} popupAttributeData={popupAttributeData} />
      </DelayedOperationController>
    </div>
  )
}

const ModalDialogComponent = <T,>(props: IModalDialogProps<T> & { popupAttributeData: object }) => {
  const [visible, setVisible] = useState(false)
  const [keyFromURL, setKeyFromURL] = useState<string>('')
  const [confirmModalVisibility, setConfirmModalVisibility] = useState<boolean>(false)
  const { finishOperations, finishOperationsInProgress, hasRegistredOperations } =
    useDelayedOperationContext()

  const { urlKey, propKey, content: ChildrenComponent, show, onClose, contentProps } = props
  const location = useLocation()
  const navigate = useNavigate()

  const closeModalAction = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      setVisible(false)
    }
  }, [onClose])

  const closeConfirmModal = useCallback(() => {
    setConfirmModalVisibility(false)
  }, [])

  const onConfirmAction = useCallback(
    (key: string) => {
      if (key === CONFIRM_BUTTON_YES_KEY) {
        finishOperations()
          .then(() => {
            closeConfirmModal()
            closeModalAction()
          })
          .catch(() => {
            // nothing to do
          })
        return
      }
      closeConfirmModal()
      if (key === CONFIRM_BUTTON_NO_KEY) {
        closeModalAction()
      }
    },
    [closeConfirmModal, closeModalAction, finishOperations]
  )

  const closeModal = useCallback(
    (e?: MouseEvent) => {
      if (e) {
        e.stopPropagation()
      }

      if (finishOperationsInProgress) {
        return
      }

      if (hasRegistredOperations) {
        setConfirmModalVisibility(true)
        return
      }

      if (urlKey) {
        const url = deleteParamsFromUrl([urlKey])
        navigate(url, location.state || {})
      }
      closeModalAction()
    },
    [
      finishOperationsInProgress,
      hasRegistredOperations,
      urlKey,
      closeModalAction,
      navigate,
      location.state,
    ]
  )

  const childrenProps = useMemo(() => {
    const data = {
      ...contentProps,
      closeModal,
    } as T & IModalContent

    if (propKey) {
      data[propKey] = keyFromURL
    }

    return data
  }, [contentProps, closeModal, propKey, keyFromURL])

  useEffect(() => {
    const checkUrl = () => {
      if (urlKey) {
        const urlObj = arrayToObject(location.pathname.split('/').filter(Boolean))
        const key = urlObj[urlKey]
        if (key) {
          setKeyFromURL(key)
          setVisible(true)
        } else {
          setVisible(false)
          if (visible && onClose) {
            onClose()
          }
        }
      }
    }

    checkUrl()
  }, [location, onClose, urlKey, visible])

  useEffect(() => {
    if (show !== undefined) {
      setVisible(show)
    }
  }, [show])

  if (!visible) {
    return null
  }

  return (
    <Portal modal={true}>
      <ModalPaper
        {...props}
        closeModal={closeModal}
        finishOperationsInProgress={finishOperationsInProgress}
        popupAttributeData={props.popupAttributeData}
      >
        <ChildrenComponent {...childrenProps} />
      </ModalPaper>
      <ConfirmDialog
        buttons={[
          { key: CONFIRM_BUTTON_YES_KEY, title: 'Yes' },
          { key: CONFIRM_BUTTON_NO_KEY, title: 'No', color: 'danger' },
          { key: CONFIRM_BUTTON_CANCEL_KEY, title: 'Cancel', color: 'grey' },
        ]}
        show={confirmModalVisibility}
        onClose={closeConfirmModal}
        onAction={onConfirmAction}
        textQuestion={'You have unsaved changes. Do you want to save before exiting?'}
      />
    </Portal>
  )
}
